// Writing a gTables workbook as an .xlsx — the reverse of data/xlsxImport.ts.
//
// Every sheet goes, and every formula Excel can run goes AS a formula, with the
// value gTables calculated stored beside it so the file reads right before
// anything recalculates. The exception is a GI.* call: Excel has no Giraffe
// project to ask, so that cell is written as the number it shows now. A
// formula that only READS such a cell (`=B4*1.1`) stays live — it reads the
// number.
//
// What is written: values, formulas, number formats, bold/italic/colour/fill/
// borders/alignment/wrap, column widths, row heights, hidden bands, frozen
// panes, the active tab, and live-block names as defined names. What is not:
// charts, database sub-rows (they are drawn beneath a row, not cells in the
// sheet), dropdowns and filters.

import type { Border, BorderEdge, CellStyle, CellValue, FormatId, Sheet, Workbook } from '@/model/types';
import { parseCk, DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT } from '@/model/workbook';
import { colToLabel, quoteSheetName } from '@/model/address';
import { localeDateOrder } from '@/model/dates';
import { containsGiCall } from '@/engine/engine';
import { writeZip } from './zipWrite';
import type { Bytes } from './zip';

export interface ExportName {
  name: string;
  sheetId: string;
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

export interface ExportSource {
  /** What the cell shows now: the computed result for a formula, the literal otherwise. */
  valueAt(sheet: Sheet, row: number, col: number): CellValue | undefined;
  /** Defined names formulas may use, e.g. live-block names. */
  names?: readonly ExportName[];
  /** ISO code for the currency formats. Defaults to USD. */
  currency?: string;
}

export interface ExportReport {
  /** GI.* formulas written as the value they showed. */
  frozen: number;
  /** Formulas kept live. */
  formulas: number;
  /** Sheets whose names Excel would refuse, and what they became. */
  renamed: { from: string; to: string }[];
}

export async function exportXlsx(wb: Workbook, source: ExportSource): Promise<{ bytes: Bytes; report: ExportReport }> {
  const { parts, report } = buildXlsxParts(wb, source);
  return { bytes: await writeZip(parts), report };
}

const NS_MAIN = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const NS_PKG_REL = 'http://schemas.openxmlformats.org/package/2006/relationships';
const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';

/** The package as text parts, keyed by path. Split from the zip so tests can read the XML. */
export function buildXlsxParts(wb: Workbook, source: ExportSource): { parts: Record<string, string>; report: ExportReport } {
  const report: ExportReport = { frozen: 0, formulas: 0, renamed: [] };
  const sheetNames = excelSheetNames(wb.sheets);
  wb.sheets.forEach((s, i) => {
    if (sheetNames[i] !== s.name) report.renamed.push({ from: s.name, to: sheetNames[i]! });
  });
  const renames = new Map(report.renamed.map((r) => [r.from.toLowerCase(), r.to]));

  // Names Excel will accept go in as defined names; a formula that uses any
  // other one could never resolve there, so it is written as its value.
  const definedNames: { name: string; ref: string }[] = [];
  const unusable = new Set<string>();
  for (const n of source.names ?? []) {
    const index = wb.sheets.findIndex((s) => s.id === n.sheetId);
    if (index < 0 || !isValidDefinedName(n.name)) {
      unusable.add(n.name.toLowerCase());
      continue;
    }
    const ref = `${quoteSheetName(sheetNames[index]!)}!${absolute(n.r1, n.c1)}:${absolute(n.r2, n.c2)}`;
    definedNames.push({ name: n.name, ref });
  }

  const styles = new StyleTable(source.currency ?? 'USD');
  const activeIndex = Math.max(0, wb.sheets.findIndex((s) => s.id === wb.activeSheetId));

  const parts: Record<string, string> = {};
  parts['[Content_Types].xml'] = contentTypes(wb.sheets.length);
  parts['_rels/.rels'] =
    `${XML_HEAD}<Relationships xmlns="${NS_PKG_REL}">` +
    `<Relationship Id="rId1" Type="${NS_REL}/officeDocument" Target="xl/workbook.xml"/>` +
    '</Relationships>';

  const sheetTags = sheetNames
    .map((name, i) => `<sheet name="${attr(name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join('');
  const namesXml = definedNames.length
    ? `<definedNames>${definedNames.map((d) => `<definedName name="${attr(d.name)}">${text(d.ref)}</definedName>`).join('')}</definedNames>`
    : '';
  parts['xl/workbook.xml'] =
    `${XML_HEAD}<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">` +
    `<bookViews><workbookView activeTab="${activeIndex}"/></bookViews>` +
    `<sheets>${sheetTags}</sheets>${namesXml}` +
    // Recalculate on open: the stored values are gTables' answers, and Excel's
    // own should replace them the moment it can work them out.
    '<calcPr calcId="191029" fullCalcOnLoad="1"/>' +
    '</workbook>';

  const rels = wb.sheets
    .map((_, i) => `<Relationship Id="rId${i + 1}" Type="${NS_REL}/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`)
    .join('');
  parts['xl/_rels/workbook.xml.rels'] =
    `${XML_HEAD}<Relationships xmlns="${NS_PKG_REL}">${rels}` +
    `<Relationship Id="rId${wb.sheets.length + 1}" Type="${NS_REL}/styles" Target="styles.xml"/>` +
    '</Relationships>';

  wb.sheets.forEach((sheet, i) => {
    parts[`xl/worksheets/sheet${i + 1}.xml`] = worksheet(sheet, i === activeIndex, {
      source,
      styles,
      report,
      rewrite: (f) => toExcelFormula(f, renames),
      usesUnusableName: (f) => unusable.size > 0 && namesIn(f).some((n) => unusable.has(n.toLowerCase())),
    });
  });
  // Last, because the worksheets are what fill the table.
  parts['xl/styles.xml'] = styles.xml();

  return { parts, report };
}

// ---------------------------------------------------------------------------
// Worksheets
// ---------------------------------------------------------------------------

interface SheetContext {
  source: ExportSource;
  styles: StyleTable;
  report: ExportReport;
  rewrite: (formula: string) => string;
  usesUnusableName: (formula: string) => boolean;
}

/** The inverse of the reader's widthToPx / pointsToPx. */
const pxToWidth = (px: number): number => round2(Math.max(0, (px - 5) / 7));
const pxToPoints = (px: number): number => round2((px * 3) / 4);

function worksheet(sheet: Sheet, selected: boolean, ctx: SheetContext): string {
  const rows = new Map<number, { col: number; xml: string }[]>();
  let maxRow = 0;
  let maxCol = 0;

  for (const [key, cell] of Object.entries(sheet.cells)) {
    const { row, col } = parseCk(key);
    const xml = cellXml(sheet, row, col, cell, ctx);
    if (xml === null) continue;
    let list = rows.get(row);
    if (!list) rows.set(row, (list = []));
    list.push({ col, xml });
    maxRow = Math.max(maxRow, row);
    maxCol = Math.max(maxCol, col);
  }

  // A row with no cells still needs a <row> when it has a height or is hidden.
  const rowNumbers = new Set<number>(rows.keys());
  for (const r of Object.keys(sheet.rowHeights)) rowNumbers.add(Number(r));
  for (const r of Object.keys(sheet.hiddenRows ?? {})) rowNumbers.add(Number(r));

  const body = [...rowNumbers]
    .sort((a, b) => a - b)
    .map((r) => {
      const cells = (rows.get(r) ?? []).sort((a, b) => a.col - b.col).map((c) => c.xml).join('');
      const height = sheet.rowHeights[r];
      const ht = height !== undefined ? ` ht="${pxToPoints(height)}" customHeight="1"` : '';
      const hidden = sheet.hiddenRows?.[r] ? ' hidden="1"' : '';
      return `<row r="${r}"${ht}${hidden}>${cells}</row>`;
    })
    .join('');

  const colNumbers = new Set<number>();
  for (const c of Object.keys(sheet.colWidths)) colNumbers.add(Number(c));
  for (const c of Object.keys(sheet.hiddenCols ?? {})) colNumbers.add(Number(c));
  const cols = [...colNumbers]
    .sort((a, b) => a - b)
    .map((c) => {
      const width = pxToWidth(sheet.colWidths[c] ?? DEFAULT_COL_WIDTH);
      const hidden = sheet.hiddenCols?.[c] ? ' hidden="1"' : '';
      return `<col min="${c}" max="${c}" width="${width}" customWidth="1"${hidden}/>`;
    })
    .join('');

  const dimension = maxRow > 0 ? `A1:${colToLabel(maxCol)}${maxRow}` : 'A1';
  return (
    `${XML_HEAD}<worksheet xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">` +
    `<dimension ref="${dimension}"/>` +
    `<sheetViews><sheetView workbookViewId="0"${selected ? ' tabSelected="1"' : ''}>${pane(sheet)}</sheetView></sheetViews>` +
    // gTables' defaults are wider and taller than Excel's, and every column
    // that was never resized should still look the way it did here.
    `<sheetFormatPr defaultColWidth="${pxToWidth(DEFAULT_COL_WIDTH)}" defaultRowHeight="${pxToPoints(DEFAULT_ROW_HEIGHT)}" customHeight="1"/>` +
    (cols ? `<cols>${cols}</cols>` : '') +
    `<sheetData>${body}</sheetData>` +
    mergeCellsXml(sheet) +
    '</worksheet>'
  );
}

/** OOXML requires `<mergeCells>` right after `<sheetData>`, before anything else. */
function mergeCellsXml(sheet: Sheet): string {
  const merges = (sheet.merges ?? []).filter((m) => m.r2 > m.r1 || m.c2 > m.c1);
  if (merges.length === 0) return '';
  const refs = merges
    .map((m) => `<mergeCell ref="${colToLabel(m.c1)}${m.r1}:${colToLabel(m.c2)}${m.r2}"/>`)
    .join('');
  return `<mergeCells count="${merges.length}">${refs}</mergeCells>`;
}

function pane(sheet: Sheet): string {
  const rows = sheet.freezeRows ?? 0;
  const cols = sheet.freezeCols ?? 0;
  if (rows <= 0 && cols <= 0) return '';
  const active = rows > 0 && cols > 0 ? 'bottomRight' : rows > 0 ? 'bottomLeft' : 'topRight';
  const x = cols > 0 ? ` xSplit="${cols}"` : '';
  const y = rows > 0 ? ` ySplit="${rows}"` : '';
  return `<pane${x}${y} topLeftCell="${colToLabel(cols + 1)}${rows + 1}" activePane="${active}" state="frozen"/>`;
}

function cellXml(
  sheet: Sheet,
  row: number,
  col: number,
  cell: Sheet['cells'][string],
  ctx: SheetContext
): string | null {
  const ref = `${colToLabel(col)}${row}`;
  const s = ctx.styles.index(cell.fmt, cell.s);
  const sAttr = s > 0 ? ` s="${s}"` : '';

  if (cell.f !== undefined) {
    const value = ctx.source.valueAt(sheet, row, col) ?? null;
    if (containsGiCall(cell.f) || ctx.usesUnusableName(cell.f)) {
      ctx.report.frozen++;
      return literalXml(ref, sAttr, value, true);
    }
    ctx.report.formulas++;
    const f = `<f>${text(ctx.rewrite(cell.f))}</f>`;
    if (typeof value === 'number' && Number.isFinite(value)) return `<c r="${ref}"${sAttr}>${f}<v>${value}</v></c>`;
    if (typeof value === 'boolean') return `<c r="${ref}"${sAttr} t="b">${f}<v>${value ? 1 : 0}</v></c>`;
    if (typeof value === 'string' && EXCEL_ERRORS.has(value)) return `<c r="${ref}"${sAttr} t="e">${f}<v>${value}</v></c>`;
    // An error Excel has no code for (#CIRC!) is left for Excel to work out.
    if (typeof value === 'string' && !/^#[A-Z0-9/]+[!?]$/.test(value)) {
      return `<c r="${ref}"${sAttr} t="str">${f}<v>${text(value)}</v></c>`;
    }
    return `<c r="${ref}"${sAttr}>${f}</c>`;
  }

  return literalXml(ref, sAttr, cell.v ?? null, false);
}

const EXCEL_ERRORS = new Set(['#NULL!', '#DIV/0!', '#VALUE!', '#REF!', '#NAME?', '#NUM!', '#N/A']);

/**
 * A value with no formula. `computed` is a GI.* result: an error there is an
 * honest error, so it goes in as one; a literal string that happens to read
 * "#DIV/0!" is text someone typed (or an import kept), and stays text.
 */
function literalXml(ref: string, sAttr: string, value: CellValue, computed: boolean): string | null {
  if (value === null || value === '') return sAttr ? `<c r="${ref}"${sAttr}/>` : null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? `<c r="${ref}"${sAttr}><v>${value}</v></c>` : `<c r="${ref}"${sAttr} t="e"><v>#NUM!</v></c>`;
  }
  if (typeof value === 'boolean') return `<c r="${ref}"${sAttr} t="b"><v>${value ? 1 : 0}</v></c>`;
  if (computed && EXCEL_ERRORS.has(value)) return `<c r="${ref}"${sAttr} t="e"><v>${value}</v></c>`;
  return `<c r="${ref}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${text(value)}</t></is></c>`;
}

// ---------------------------------------------------------------------------
// Formulas
// ---------------------------------------------------------------------------

/**
 * Functions newer than Excel 2007 are stored in the file with a prefix, and an
 * unprefixed TEXTJOIN in the XML reads back as #NAME? even in an Excel that has
 * it. Only the ones a gTables formula can plausibly hold are listed.
 */
const FUTURE_FUNCTIONS = new Set([
  'AGGREGATE', 'ARABIC', 'BASE', 'BITAND', 'BITLSHIFT', 'BITOR', 'BITRSHIFT', 'BITXOR', 'CEILING.MATH',
  'CEILING.PRECISE', 'CHOOSECOLS', 'CHOOSEROWS', 'CONCAT', 'COVARIANCE.P', 'COVARIANCE.S', 'DAYS', 'DECIMAL',
  'DROP', 'EXPAND', 'FLOOR.MATH', 'FLOOR.PRECISE', 'FORMULATEXT', 'HSTACK', 'IFNA', 'IFS', 'ISFORMULA',
  'ISOMITTED', 'ISOWEEKNUM', 'LAMBDA', 'LET', 'MAXIFS', 'MINIFS', 'MODE.MULT', 'MODE.SNGL', 'NETWORKDAYS.INTL',
  'NORM.DIST', 'NORM.INV', 'NORM.S.DIST', 'NORM.S.INV', 'NUMBERVALUE', 'PDURATION', 'PERCENTILE.EXC',
  'PERCENTILE.INC', 'QUARTILE.EXC', 'QUARTILE.INC', 'RANDARRAY', 'RANK.AVG', 'RANK.EQ', 'RRI', 'SEQUENCE',
  'SHEET', 'SHEETS', 'STDEV.P', 'STDEV.S', 'SWITCH', 'TAKE', 'TEXTAFTER', 'TEXTBEFORE', 'TEXTJOIN',
  'TEXTSPLIT', 'TOCOL', 'TOROW', 'UNICHAR', 'UNICODE', 'VALUETOTEXT', 'ARRAYTOTEXT', 'VAR.P',
  'VAR.S', 'VSTACK', 'WORKDAY.INTL', 'WRAPCOLS', 'WRAPROWS', 'XLOOKUP', 'XMATCH', 'XOR',
]);
/**
 * These four carry a second prefix, for reasons known only to Excel — they
 * were implemented as worksheet-level functions rather than calculation-engine
 * ones, and the file format remembers that split. `SORTBY` and `UNIQUE` were
 * missing from here before (filed under the single-prefix set instead), which
 * is the same class of bug as no prefix at all: a defined name or cell formula
 * built with either reads back as unrecoverable content, not just `#NAME?`.
 */
const WORKSHEET_FUNCTIONS = new Set(['FILTER', 'SORT', 'SORTBY', 'UNIQUE']);

/**
 * A gTables formula as Excel stores it: future functions prefixed, and any
 * sheet that had to be renamed for Excel referred to by its new name.
 */
export function toExcelFormula(formula: string, renames: ReadonlyMap<string, string> = new Map()): string {
  return walkTokens(formula, {
    call(name) {
      const upper = name.toUpperCase();
      if (WORKSHEET_FUNCTIONS.has(upper)) return `_xlfn._xlws.${name}`;
      return FUTURE_FUNCTIONS.has(upper) ? `_xlfn.${name}` : name;
    },
    sheet(name, raw) {
      const to = renames.get(name.toLowerCase());
      return to === undefined ? raw : quoteSheetName(to);
    },
  });
}

/** A formula with its sheet references renamed, keyed by the old name lowercased. Nothing else changes. */
export function renameSheetRefs(formula: string, renames: ReadonlyMap<string, string>): string {
  return walkTokens(formula, {
    sheet(name, raw) {
      const to = renames.get(name.toLowerCase());
      return to === undefined ? raw : quoteSheetName(to);
    },
  });
}

/** The reverse, for the importer: Excel's storage prefixes dropped. */
export function fromExcelFormula(formula: string): string {
  return walkTokens(formula, { call: (name) => name.replace(/^(_xlfn\.)?(_xlws\.)?/i, '') });
}

/** Bare identifiers that are not calls, sheet prefixes, or booleans — candidate defined names. */
function namesIn(formula: string): string[] {
  const out: string[] = [];
  walkTokens(formula, {
    name(name) {
      out.push(name);
      return name;
    },
  });
  return out;
}

interface TokenHandlers {
  call?: (name: string) => string;
  sheet?: (name: string, raw: string) => string;
  name?: (name: string) => string;
}

/**
 * Walk a formula's identifiers outside string literals, letting each kind be
 * rewritten. Deliberately shallow — it knows quotes, `name(`, `name!` and
 * `'quoted name'!`, which is all the rewriting above needs.
 */
function walkTokens(formula: string, h: TokenHandlers): string {
  let out = '';
  let i = 0;
  const n = formula.length;
  while (i < n) {
    const ch = formula[i]!;
    if (ch === '"') {
      let j = i + 1;
      while (j < n) {
        if (formula[j] === '"') {
          if (formula[j + 1] === '"') j += 2;
          else break;
        } else j++;
      }
      out += formula.slice(i, j + 1);
      i = j + 1;
      continue;
    }
    if (ch === "'") {
      let j = i + 1;
      while (j < n) {
        if (formula[j] === "'") {
          if (formula[j + 1] === "'") j += 2;
          else break;
        } else j++;
      }
      const raw = formula.slice(i, j + 1);
      const name = raw.slice(1, -1).replace(/''/g, "'");
      out += formula[j + 1] === '!' && h.sheet ? h.sheet(name, raw) : raw;
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_\\]/.test(ch) && (i === 0 || !/[A-Za-z0-9_.$]/.test(formula[i - 1]!))) {
      let j = i + 1;
      while (j < n && /[A-Za-z0-9_.]/.test(formula[j]!)) j++;
      const ident = formula.slice(i, j);
      let k = j;
      while (k < n && formula[k] === ' ') k++;
      if (formula[k] === '(') out += h.call ? h.call(ident) : ident;
      else if (formula[j] === '!') out += h.sheet ? h.sheet(ident, ident) : ident;
      else if (h.name && isNamePosition(formula, i, j) && !isCellLike(ident) && !/^(TRUE|FALSE)$/i.test(ident)) {
        out += h.name(ident);
      }
      else out += ident;
      i = j;
      continue;
    }
    out += ch;
    i++;
  }
  return out;
}

/** Not half of a reference: `A$1`, `A:A` and `Sheet1!Name` all have an identifier that is not a name. */
function isNamePosition(formula: string, start: number, end: number): boolean {
  const next = formula[end];
  const prev = formula[start - 1];
  return next !== '$' && next !== ':' && prev !== ':' && prev !== '!';
}

/** A1, XFD1048576, or an R1C1 form — things that look like a name and are not one. */
function isCellLike(s: string): boolean {
  const a1 = /^([A-Za-z]{1,3})(\d+)$/.exec(s);
  if (a1 && labelValue(a1[1]!) <= 16384) return true;
  return /^(R\d*C?\d*|C\d*)$/i.test(s);
}

function labelValue(label: string): number {
  let n = 0;
  for (const ch of label.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

function isValidDefinedName(name: string): boolean {
  return /^[A-Za-z_\\][A-Za-z0-9_.]*$/.test(name) && !isCellLike(name) && !/^(TRUE|FALSE)$/i.test(name) && name.length <= 255;
}

function absolute(row: number, col: number): string {
  return `$${colToLabel(col)}$${row}`;
}

// ---------------------------------------------------------------------------
// Sheet names
// ---------------------------------------------------------------------------

/**
 * Excel refuses a sheet name over 31 characters, one with any of `[]:*?/\`, or
 * one that starts or ends with an apostrophe — gTables refuses none of them.
 * Each is made acceptable and kept unique, ignoring case as Excel does.
 */
function excelSheetNames(sheets: readonly Sheet[]): string[] {
  const taken = new Set<string>();
  return sheets.map((s) => {
    const base = s.name.replace(/[[\]:*?/\\]/g, '_').replace(/^'+|'+$/g, '').trim().slice(0, 31) || 'Sheet';
    let name = base;
    for (let i = 2; taken.has(name.toLowerCase()); i++) {
      const suffix = ` (${i})`;
      name = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    }
    taken.add(name.toLowerCase());
    return name;
  });
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

/**
 * A gTables format as an Excel code, written so the importer maps it straight
 * back: `formatFromCode` reads each of these as the format it came from.
 */
function formatCode(fmt: FormatId, currency: string): string | number {
  const sym = `[$${currencySymbol(currency)}]`;
  switch (fmt) {
    case 'number0':
      return 3;
    case 'number1':
      return '#,##0.0';
    case 'number2':
      return 4;
    case 'currency0':
      return `${sym}#,##0`;
    case 'currency2':
      return `${sym}#,##0.00`;
    case 'percent0':
      return 9;
    case 'percent1':
      return '0.0%';
    case 'percent2':
      return '0.00%';
    case 'date': {
      // The same day/month order the grid shows, so 03/04 means one thing.
      const order = localeDateOrder();
      return order === 'mdy' ? 'mm/dd/yyyy' : order === 'ymd' ? 'yyyy-mm-dd' : 'dd/mm/yyyy';
    }
    case 'dateLong':
      return 'd mmm yyyy';
    case 'monthYear':
      return 'mmm yyyy';
    case 'text':
      return 49;
    case 'general':
    default:
      return 0;
  }
}

/**
 * The symbol the grid shows — `A$` in Sydney for AUD. A locale that only knows
 * the code (`USD`) falls back to the narrow symbol, which is what Excel shows.
 */
function currencySymbol(code: string): string {
  const symbol = (display: 'symbol' | 'narrowSymbol'): string | undefined => {
    try {
      const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency: code, currencyDisplay: display }).formatToParts(0);
      return parts.find((p) => p.type === 'currency')?.value;
    } catch {
      return undefined;
    }
  };
  const wide = symbol('symbol');
  return (wide && wide !== code ? wide : symbol('narrowSymbol')) ?? '$';
}

/** Interns fonts, fills, borders and cell formats, so N styled cells share a handful of entries. */
class StyleTable {
  private numFmts = new Map<string, number>();
  private fonts = ['<font><sz val="11"/><name val="Calibri"/></font>'];
  private fills = ['<fill><patternFill patternType="none"/></fill>', '<fill><patternFill patternType="gray125"/></fill>'];
  private borders = ['<border><left/><right/><top/><bottom/><diagonal/></border>'];
  private xfs = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'];
  private byKey = new Map<string, number>([[this.xfs[0]!, 0]]);

  constructor(private currency: string) {}

  index(fmt: FormatId | undefined, style: CellStyle | undefined): number {
    const numFmtId = fmt ? this.numFmtId(fmt) : 0;
    const fontId = style ? this.intern(this.fonts, fontXml(style)) : 0;
    const fill = style?.bg ? argb(style.bg) : undefined;
    const fillId = fill
      ? this.intern(this.fills, `<fill><patternFill patternType="solid"><fgColor rgb="${fill}"/><bgColor indexed="64"/></patternFill></fill>`)
      : 0;
    const borderId = style?.borders ? this.intern(this.borders, borderXml(style.borders)) : 0;
    const align = style?.align ? ` horizontal="${style.align}"` : '';
    const wrap = style?.overflow === 'wrap' ? ' wrapText="1"' : '';
    const alignment = align || wrap ? `<alignment${align}${wrap}/>` : '';

    const xf =
      `<xf numFmtId="${numFmtId}" fontId="${fontId}" fillId="${fillId}" borderId="${borderId}" xfId="0"` +
      `${numFmtId ? ' applyNumberFormat="1"' : ''}${fontId ? ' applyFont="1"' : ''}${fillId ? ' applyFill="1"' : ''}` +
      `${borderId ? ' applyBorder="1"' : ''}${alignment ? ' applyAlignment="1"' : ''}` +
      (alignment ? `>${alignment}</xf>` : '/>');
    let i = this.byKey.get(xf);
    if (i === undefined) {
      i = this.xfs.length;
      this.xfs.push(xf);
      this.byKey.set(xf, i);
    }
    return i;
  }

  private numFmtId(fmt: FormatId): number {
    const code = formatCode(fmt, this.currency);
    if (typeof code === 'number') return code;
    let id = this.numFmts.get(code);
    if (id === undefined) {
      // Custom formats start at 164; everything below is Excel's built-ins.
      id = 164 + this.numFmts.size;
      this.numFmts.set(code, id);
    }
    return id;
  }

  private intern(list: string[], xml: string): number {
    const at = list.indexOf(xml);
    if (at >= 0) return at;
    list.push(xml);
    return list.length - 1;
  }

  xml(): string {
    const numFmts = [...this.numFmts]
      .map(([code, id]) => `<numFmt numFmtId="${id}" formatCode="${attr(code)}"/>`)
      .join('');
    return (
      `${XML_HEAD}<styleSheet xmlns="${NS_MAIN}">` +
      (numFmts ? `<numFmts count="${this.numFmts.size}">${numFmts}</numFmts>` : '') +
      `<fonts count="${this.fonts.length}">${this.fonts.join('')}</fonts>` +
      `<fills count="${this.fills.length}">${this.fills.join('')}</fills>` +
      `<borders count="${this.borders.length}">${this.borders.join('')}</borders>` +
      '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
      `<cellXfs count="${this.xfs.length}">${this.xfs.join('')}</cellXfs>` +
      '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
      '</styleSheet>'
    );
  }
}

function fontXml(style: CellStyle): string {
  const color = style.color ? argb(style.color) : undefined;
  return (
    '<font>' +
    (style.bold ? '<b/>' : '') +
    (style.italic ? '<i/>' : '') +
    '<sz val="11"/>' +
    (color ? `<color rgb="${color}"/>` : '') +
    '<name val="Calibri"/></font>'
  );
}

function borderXml(borders: Partial<Record<BorderEdge, Border>>): string {
  const edge = (name: BorderEdge): string => {
    const b = borders[name];
    if (!b || b.w <= 0) return `<${name}/>`;
    const style = b.w >= 1.5 ? 'thick' : b.w >= 1 ? 'medium' : 'thin';
    const color = argb(b.c) ?? 'FF000000';
    return `<${name} style="${style}"><color rgb="${color}"/></${name}>`;
  };
  return `<border>${edge('left')}${edge('right')}${edge('top')}${edge('bottom')}<diagonal/></border>`;
}

/** `#c00` or `#cc0000` to Excel's opaque ARGB. Anything else (a CSS name, rgb()) is left unset. */
function argb(css: string): string | undefined {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(css.trim());
  if (!m) return undefined;
  const hex = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  return `FF${hex.toUpperCase()}`;
}

// ---------------------------------------------------------------------------
// Package plumbing
// ---------------------------------------------------------------------------

function contentTypes(sheetCount: number): string {
  const sheets = Array.from(
    { length: sheetCount },
    (_, i) =>
      `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
  ).join('');
  return (
    `${XML_HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    sheets +
    '</Types>'
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * XML text. Control characters XML 1.0 cannot carry are written the way Excel
 * writes them, `_x0001_`, which the reader already undoes.
 */
function text(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, (c) => `_x${c.charCodeAt(0).toString(16).padStart(4, '0').toUpperCase()}_`);
}

function attr(s: string): string {
  return text(s).replace(/"/g, '&quot;');
}
